import {
    createContext,
    ElementType,
    PropsWithChildren,
    ReactNode,
    useCallback,
    useContext,
    useRef,
    useState
} from 'react';
import Container from './Container';
import BottomSheet from './BottomSheet';

type BottomSheetContextType = {
    expand: (
        content: ElementType,
    ) => void,
    close: () => void,
    onClose: () => void
};

const BottomSheetContext = createContext<BottomSheetContextType | undefined>(undefined);

const BottomSheetProvider = ({
    children
}: PropsWithChildren) => {
    const ref = useRef<typeof BottomSheet>(null);
    const [content, setContent] = useState<ReactNode>(null);

    const expand = useCallback((content: ReactNode) => {
        setContent(content);

        ref.current?.expand();
    }, []);

    const close = useCallback(() => {
        setContent(null);

        ref.current?.close();
    }, []);

    const onClose = useCallback(() => {
        setContent(null);

        ref.current?.close();
    }, []);

    return <BottomSheetContext.Provider value={{expand, close, onClose}}>
        <Container
            onRawClick={onClose}
            position={content ? {
                type: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: '25%',
            } : undefined}
            z={content ? 1 : undefined}
        />

        {children}

        <BottomSheet
            ref={ref}
            percentage='25%'
            onClose={onClose}
            z={2}
        >
            {content}
        </BottomSheet>
    </BottomSheetContext.Provider>
};

const useBottomSheet = () => {
    const context = useContext(BottomSheetContext);

    if (!context) {
        throw new Error('useBottomSheet must be used within a BottomSheetProvider');
    }

    return context;
};

export {
    BottomSheetProvider,
    useBottomSheet
};